import { FC } from 'react';
import { COMPARE_INSIGHT_OPTIONS, MAX_COMPARE_INSIGHTS } from './settingsOptions';

interface CompareInsightsPickerProps {
  value: string[];
  disabled?: boolean;
  onChange: (insights: string[]) => void;
}

const labelFor = (id: string) =>
  COMPARE_INSIGHT_OPTIONS.find(({ value }) => value === id)?.label ?? id;

// Behaves like the Compare view's insight picker (SettingsInline): 1–3 ordered stats,
// the first can move down and the last up. Every change is saved straight away.
const CompareInsightsPicker: FC<CompareInsightsPickerProps> = ({ value, disabled, onChange }) => {
  const isFull = value.length >= MAX_COMPARE_INSIGHTS;
  const unselected = COMPARE_INSIGHT_OPTIONS.filter(({ value: id }) => !value.includes(id));
  // Empty slots show as "Insight 2", "Insight 3" until a stat is picked.
  const emptySlots = Array.from(
    { length: MAX_COMPARE_INSIGHTS - value.length },
    (_, i) => value.length + i + 1
  );

  const toggle = (id: string) => {
    if (value.includes(id)) {
      if (value.length > 1) onChange(value.filter((item) => item !== id));
    } else if (!isFull) {
      onChange([...value, id]);
    }
  };

  const swap = (index: number, other: number) => {
    const next = [...value];
    [next[index], next[other]] = [next[other], next[index]];
    onChange(next);
  };

  return (
    <div className="settings-card settings-compare">
      <div className="settings-compare__header">
        <h3 className="settings-compare__title">Compare Stats</h3>
        <img src="assets/icon/settings/compare-title-divider.svg" alt="" />
      </div>

      <div className="settings-compare__lists">
        <div className="settings-compare__list">
          {value.map((id, index) => (
            <div key={id} className="settings-compare__row">
              <button
                className="settings-compare__stat settings-compare__stat--selected"
                disabled={disabled}
                onClick={() => toggle(id)}
                aria-label={`Deselect ${labelFor(id)}`}
              >
                <img src="assets/icon/settings/compare-dot-selected.png" alt="" />
                <span>{labelFor(id)}</span>
              </button>
              {index === 0 && value.length > 1 && (
                <button
                  className="settings-compare__arrow settings-compare__arrow--down"
                  disabled={disabled}
                  onClick={() => swap(index, index + 1)}
                  aria-label="Move down"
                />
              )}
              {index === value.length - 1 && index > 0 && (
                <button
                  className="settings-compare__arrow settings-compare__arrow--up"
                  disabled={disabled}
                  onClick={() => swap(index, index - 1)}
                  aria-label="Move up"
                />
              )}
            </div>
          ))}
          {emptySlots.map((slot) => (
            <div key={slot} className="settings-compare__placeholder">
              Insight {slot}
            </div>
          ))}
        </div>

        <div className="settings-compare__list">
          <img src="assets/icon/settings/compare-list-divider.svg" alt="" />
          {unselected.map(({ value: id, label }) => (
            <button
              key={id}
              className="settings-compare__stat"
              disabled={disabled || isFull}
              onClick={() => toggle(id)}
              aria-label={`Select ${label}`}
            >
              <img src="assets/icon/settings/compare-dot.svg" alt="" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompareInsightsPicker;
